import type { ReactNode, SyntheticEvent } from 'react';

/**
 * Stops React-tree propagation of user-interaction events at a portal
 * boundary, without touching native DOM propagation.
 *
 * Why this exists: react-aria renders every popover into a portal on
 * `document.body`, but React propagates synthetic events up the REACT tree —
 * the popover's React ancestors are the trigger's ancestors. So a click on a
 * search input inside a popover opened from a pipeline card bubbled to the
 * card root's `onClick` (opening the record slideout) and to dnd-kit's
 * `onPointerDown`/`onKeyDown` listeners on the same root; the same leak hits
 * any popover rendered under a table row's `onRowClick`, a clickable card, or
 * any other activate-on-click ancestor. Trigger buttons defend themselves with
 * `useDndContainment`, but nothing defended the popover's CONTENT.
 *
 * Why not `e.stopPropagation()`: React's synthetic `stopPropagation()` also
 * calls `stopPropagation()` on the NATIVE event. React's listener for a
 * body-level portal sits on `document.body`, so a native stop there hides the
 * event from every bubble-phase `document`/`window` listener — global
 * shortcuts (Cmd+S in SaveBar, Cmd+Enter in CrmCreateForm, nav shortcuts) and
 * hand-rolled click-outside handlers would silently stop firing whenever the
 * user interacts inside a popover. Marking the synthetic event as stopped is
 * enough: React's dispatch loop checks `event.isPropagationStopped()` between
 * fiber instances (react-dom `processDispatchQueueItemsInOrder`) and skips
 * the remaining ancestors, while the native event bubbles on untouched.
 * Handlers on the SAME element as this boundary still run (React only checks
 * between distinct instances). Capture-phase ancestor handlers
 * (`onClickCapture`) run before this boundary and are unaffected — that is
 * what `PipelineBoard`'s post-drag click suppression relies on.
 *
 * Only pointer/mouse/touch/keyboard interaction events are contained. Focus,
 * form (`change`/`input`/`submit`), scroll and wheel events keep bubbling —
 * ancestors legitimately observe those (a popover inside a `<form>` still
 * submits it).
 *
 * `portal-event-boundary.test.tsx` pins both halves of the contract: the
 * React ancestor never sees the event, and a native `document` bubble
 * listener still does. If a React upgrade changes the dispatch check, that
 * test fails loudly instead of the containment silently degrading.
 */
function stopReactPropagation(e: SyntheticEvent): void {
  e.isPropagationStopped = () => true;
}

/** The interaction events contained by the boundary, all sharing one handler
 *  — module-level so the props object is a single stable reference. */
const CONTAINED_EVENT_HANDLERS = {
  onClick: stopReactPropagation,
  onDoubleClick: stopReactPropagation,
  onContextMenu: stopReactPropagation,
  onMouseDown: stopReactPropagation,
  onMouseUp: stopReactPropagation,
  onPointerDown: stopReactPropagation,
  onPointerUp: stopReactPropagation,
  onTouchStart: stopReactPropagation,
  onTouchEnd: stopReactPropagation,
  onKeyDown: stopReactPropagation,
  onKeyUp: stopReactPropagation,
} as const;

/**
 * Wrap a portaled overlay ELEMENT (the react-aria `Popover` itself, not its
 * children) so interaction events inside it never reach the trigger's React
 * ancestors (see the module doc-comment). Placement matters: React skips the
 * boundary's ancestors, so anything wrapped INSIDE the popover root would
 * leave react-aria's own handlers on that root (Escape-to-close via
 * `useOverlay`) among the skipped — Escape would stop closing the popover.
 * `portal-event-boundary.test.tsx` covers that too.
 *
 * Renders a `display: contents` element at the trigger site, so it takes no
 * part in layout — and while the popover is closed it is empty.
 */
export function PortalEventBoundary({ children }: { children: ReactNode }) {
  return (
    <div className='contents' {...CONTAINED_EVENT_HANDLERS}>
      {children}
    </div>
  );
}
